import { BundleBuilderStoreMixinComponent } from '@theme/bundle-builder-mixin-store';
import { emptyActiveFilters } from '@theme/bundle-builder-store';

/**
 * @typedef {import('./bundle-builder-store.js').BundleBuilderState} BundleBuilderState
 * @typedef {import('./bundle-builder-store.js').ActiveFilters} ActiveFilters
 * @typedef {import('./bundle-builder-store.js').FilterFacet} FilterFacet
 */

/**
 * @typedef {object} FacetConfig
 * @property {FilterFacet} key
 * @property {string} label
 */

/**
 * Client-side PLP facets for the Build-a-Box grid.
 * Hides SSR cards and empty category sections; never mutates `items`.
 *
 * Drawer nodes live inside `dialog-component`, so they are not parent refs.
 *
 * @typedef {object} BundleBuilderFiltersRefs
 * @property {HTMLButtonElement} desktopClear
 * @property {HTMLElement} productCount
 * @property {HTMLElement} mobileProductCount
 * @property {HTMLElement} mobileCount
 *
 * @extends {BundleBuilderStoreMixinComponent<BundleBuilderFiltersRefs>}
 */
export class BundleBuilderFiltersComponent extends BundleBuilderStoreMixinComponent {
  requiredRefs = ['desktopClear', 'productCount', 'mobileProductCount', 'mobileCount'];

  /** @type {string | null} Null until first `#ensureFacetOptions` — empty facets also signature `''`. */
  #optionsSignature = null;

  /** @type {((event: Event) => void) | null} */
  #emptyClearHandler = null;

  /** @type {ActiveFilters | undefined} */
  #activeFilters;

  /** @type {boolean} */
  #fitting = false;

  #resizeObserver = new ResizeObserver(() => {
    this.#fitHorizontalFacets();
  });

  connectedCallback() {
    super.connectedCallback();
    this.#bindEmptyClear();

    const bar = this.#desktopBar();

    if (bar) this.#resizeObserver.observe(bar);
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#unbindEmptyClear();
    super.disconnectedCallback();
  }

  /**
   * @param {BundleBuilderState} state
   */
  onStateUpdate(state) {
    const optionsChanged = this.#ensureFacetOptions();
    // Store updates keep the same activeFilters ref when only items/qty change.
    const filtersChanged = state.activeFilters !== this.#activeFilters;

    this.#activeFilters = state.activeFilters;

    // Facet inject needs reflow + checkbox sync; visibility only when filters change.
    if (filtersChanged || optionsChanged) {
      this.#fitHorizontalFacets();
    }

    if (!filtersChanged) return;

    this.#applyVisibility(state.activeFilters);
    this.#updateChrome();
  }

  clearAllFilters() {
    this.mutations.clearFilters();
  }

  #bindEmptyClear() {
    const button = this.#getEmptyState()?.querySelector('[data-bundle-filters-empty-clear]');

    if (!(button instanceof HTMLElement)) return;

    this.#emptyClearHandler = () => {
      this.clearAllFilters();
    };
    button.addEventListener('click', this.#emptyClearHandler);
  }

  #unbindEmptyClear() {
    if (!this.#emptyClearHandler) return;

    const button = this.#getEmptyState()?.querySelector('[data-bundle-filters-empty-clear]');

    if (button instanceof HTMLElement) {
      button.removeEventListener('click', this.#emptyClearHandler);
    }

    this.#emptyClearHandler = null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getEmptyState() {
    const empty = this.closest('.bundle-builder__product-cards-wrapper')?.querySelector('[data-bundle-filters-empty]');

    return empty instanceof HTMLElement ? empty : null;
  }

  /**
   * @returns {FacetConfig[]}
   */
  #facetConfig() {
    try {
      const parsed = JSON.parse(this.dataset.facets || '[]');

      if (!Array.isArray(parsed)) return [];

      /** @type {FacetConfig[]} */
      const facets = [];

      for (const entry of parsed) {
        if (!entry || typeof entry.key !== 'string' || typeof entry.label !== 'string') continue;
        if (!entry.key.trim() || !entry.label.trim()) continue;

        facets.push({
          key: /** @type {FilterFacet} */ (entry.key.trim()),
          label: entry.label.trim(),
        });
      }

      return facets;
    } catch {
      return [];
    }
  }

  /**
   * @returns {boolean} True when facet option lists were rebuilt
   */
  #ensureFacetOptions() {
    const options = this.getters.getFilterOptions();
    const facets = this.#facetConfig();
    const hasOptions = facets.some((facet) => (options[facet.key] ?? []).length > 0);
    // Always sync host visibility — empty facets signature is `''`, same as a never-run
    // string init would be, so an early return alone would leave the chrome on screen.
    this.hidden = !hasOptions;

    const signature = facets
      .map((facet) => `${facet.key}:${(options[facet.key] ?? []).join('|')}`)
      .join('::');

    if (signature === this.#optionsSignature) return false;

    this.#optionsSignature = signature;

    const desktop = this.querySelector('.facets__overflow-list');

    if (desktop instanceof HTMLElement) {
      this.#renderFacetLists(desktop, options, 'desktop', false);
    }

    const drawer = this.querySelector('[data-drawer-facets]');

    if (drawer instanceof HTMLElement) {
      this.#renderFacetLists(drawer, options, 'drawer', true);
    }

    return true;
  }

  /**
   * @param {HTMLElement} container
   * @param {ActiveFilters} options
   * @param {string} suffix
   * @param {boolean} inDrawer
   */
  #renderFacetLists(container, options, suffix, inDrawer) {
    const fragment = document.createDocumentFragment();

    for (const facet of this.#facetConfig()) {
      const values = options[facet.key];

      if (!values || values.length === 0) continue;

      fragment.appendChild(this.#createFacet(facet, values, suffix, inDrawer));
    }

    container.replaceChildren(fragment);
  }

  /**
   * @param {FacetConfig} facet
   * @param {string[]} values
   * @param {string} suffix
   * @param {boolean} inDrawer
   * @returns {HTMLElement}
   */
  #createFacet(facet, values, suffix, inDrawer) {
    const accordion = document.createElement('accordion-custom');
    accordion.className = 'facets__item';
    accordion.dataset.filterStyle = inDrawer ? 'vertical' : 'horizontal';

    if (!inDrawer) {
      accordion.setAttribute('data-disable-animation-on-desktop', 'true');
      accordion.setAttribute('data-close-with-escape', 'true');
    } else {
      accordion.setAttribute('open-by-default-on-mobile', '');
      accordion.setAttribute('open-by-default-on-desktop', '');
    }

    const details = document.createElement('details');
    details.className = 'facets__panel';
    details.dataset.facet = facet.key;

    if (!inDrawer) {
      details.dataset.autoCloseDetails = 'desktop';
    } else {
      details.open = true;
    }

    const summary = document.createElement('summary');
    summary.className = 'facets__summary';

    const label = document.createElement('span');
    label.className = 'facets__label h5';
    label.textContent = facet.label;

    const statusWrapper = document.createElement('div');
    statusWrapper.className = 'facets__status-wrapper';

    const status = document.createElement('span');
    status.className = 'facets__status';

    const statusText = document.createElement('span');
    statusText.className = 'hide-when-empty';
    statusText.dataset.facetStatus = facet.key;
    statusText.dataset.filterStyle = inDrawer ? 'vertical' : 'horizontal';

    status.appendChild(statusText);
    statusWrapper.append(status, this.#cloneTemplate('caret'));
    summary.append(label, statusWrapper);

    const panel = document.createElement('floating-panel-component');
    panel.className = 'facets__inputs facets__panel-content details-content';

    if (!inDrawer) {
      panel.dataset.closeOnResize = 'true';

      if (this.dataset.popoverScheme) {
        panel.classList.add(`color-${this.dataset.popoverScheme}`);
      }
    }

    const inputsWrapper = document.createElement('div');
    inputsWrapper.className = 'facets__inputs-wrapper';

    const list = document.createElement('ul');
    list.className = 'facets__inputs-list list-unstyled';
    list.dataset.facet = facet.key;
    list.setAttribute('role', 'group');
    list.setAttribute('aria-label', facet.label);

    for (const value of values) {
      list.appendChild(this.#createOption(facet.key, value, suffix));
    }

    inputsWrapper.appendChild(list);
    panel.appendChild(inputsWrapper);
    details.append(summary, panel);
    accordion.appendChild(details);

    return accordion;
  }

  /**
   * @param {FilterFacet} facet
   * @param {string} value
   * @param {string} suffix
   * @returns {HTMLLIElement}
   */
  #createOption(facet, value, suffix) {
    const item = document.createElement('li');
    item.className = 'facets__inputs-list-item';

    const id = `bb-filter-${suffix}-${facet}-${this.#slug(value)}`;
    const field = document.createElement('div');
    field.className = 'checkbox';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.id = id;
    input.className = 'checkbox__input';
    input.value = value;
    input.dataset.facet = facet;
    input.dataset.label = value;
    input.addEventListener('change', (event) => {
      this.#onFacetChange(event);
    });

    const optionLabel = document.createElement('label');
    optionLabel.className = 'checkbox__label';
    optionLabel.htmlFor = id;
    optionLabel.setAttribute('role', 'checkbox');

    const text = document.createElement('span');
    text.className = 'checkbox__label-text';
    text.textContent = value;

    optionLabel.append(this.#cloneTemplate('checkmark'), text);
    field.append(input, optionLabel);
    item.appendChild(field);

    return item;
  }

  /**
   * @param {Event} event
   */
  #onFacetChange(event) {
    const target = event.target;

    if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') return;

    const facet = /** @type {FilterFacet | undefined} */ (target.dataset.facet);

    if (!facet) return;

    const list = target.closest('.facets__inputs-list');

    if (!(list instanceof HTMLElement)) return;

    /** @type {string[]} */
    const checked = [];

    for (const input of list.querySelectorAll(`input[data-facet="${facet}"]:checked`)) {
      if (input instanceof HTMLInputElement) checked.push(input.value);
    }

    this.mutations.setFilterValues(facet, checked);
  }

  /**
   * Keep the selected value next to a dropdown when the row still fits.
   * If that label would push a facet into More, show a count instead — even
   * for a single value — and only then let overflow-list show More.
   */
  #fitHorizontalFacets() {
    if (this.#fitting) return;

    this.#fitting = true;
    const bar = this.#desktopBar();

    if (bar) this.#resizeObserver.unobserve(bar);

    try {
      this.#syncCheckboxState(this.#activeFilters, false);

      if (!this.#canMeasureDesktopBar()) return;

      this.#reflowDesktop();

      if (!this.#desktopOverflows()) return;

      this.#syncCheckboxState(this.#activeFilters, true);
      this.#reflowDesktop();
    } finally {
      if (bar) this.#resizeObserver.observe(bar);
      this.#fitting = false;
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #desktopBar() {
    const bar = this.querySelector('.facets--horizontal');

    return bar instanceof HTMLElement ? bar : null;
  }

  /**
   * @returns {boolean}
   */
  #canMeasureDesktopBar() {
    const bar = this.#desktopBar();

    return bar instanceof HTMLElement && bar.getClientRects().length > 0;
  }

  #reflowDesktop() {
    const list = this.querySelector('.facets__overflow-list');

    if (!(list instanceof HTMLElement)) return;

    // Facets are injected after <overflow-list> connects, so its intersection
    // observer never runs and the list stays overflow-y:hidden, clipping dropdowns.
    list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
  }

  /**
   * @returns {boolean}
   */
  #desktopOverflows() {
    const list = this.querySelector('.facets__overflow-list');

    if (!(list instanceof HTMLElement)) return false;

    const more = list.shadowRoot?.querySelector('slot[name="more"]');

    return more instanceof HTMLElement && !more.hidden;
  }

  /**
   * @param {ActiveFilters | undefined} filters
   * @param {boolean} [compactSingle] When true, a single horizontal value shows a count instead of its label
   */
  #syncCheckboxState(filters, compactSingle = false) {
    const selected = this.getters.normalizeProductFilters(filters);

    for (const input of this.querySelectorAll('input[data-facet]')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const facet = /** @type {FilterFacet | undefined} */ (input.dataset.facet);

      if (!facet) continue;

      input.checked = (selected[facet] ?? []).includes(input.value);
    }

    for (const status of this.querySelectorAll('[data-facet-status]')) {
      if (!(status instanceof HTMLElement)) continue;

      const facet = /** @type {FilterFacet | undefined} */ (status.dataset.facetStatus);

      if (!facet) continue;

      const values = selected[facet] ?? [];
      const horizontal = status.dataset.filterStyle === 'horizontal';

      status.classList.remove('bubble', 'facets__bubble');

      if (values.length === 0) {
        status.textContent = '';
      } else if (horizontal && values.length === 1 && !compactSingle) {
        status.textContent = values[0] ?? '';
      } else {
        status.textContent = String(values.length);
        status.classList.add('bubble', 'facets__bubble');
      }
    }
  }

  /**
   * @param {ActiveFilters | undefined} filters
   */
  #applyVisibility(filters) {
    const active = filters;
    const wrapper = this.closest('.bundle-builder__product-cards-wrapper');

    if (!(wrapper instanceof HTMLElement)) return;

    let visibleProductCount = 0;

    for (const category of wrapper.querySelectorAll('[data-bundle-category]')) {
      if (!(category instanceof HTMLElement)) continue;

      let visibleInCategory = 0;

      for (const card of category.querySelectorAll('bundle-builder-product-card-component')) {
        if (!(card instanceof HTMLElement)) continue;

        const matches = this.getters.productMatchesFilters(
          { filters: this.#readCardFilters(card) },
          active
        );

        card.hidden = !matches;

        if (matches) visibleInCategory += 1;
      }

      category.hidden = visibleInCategory === 0;
      visibleProductCount += visibleInCategory;
    }

    const empty = this.#getEmptyState();

    if (empty) {
      empty.hidden = !(this.getters.getActiveFilterCount() > 0 && visibleProductCount === 0);
    }

    const countText = this.#formatCount(
      visibleProductCount,
      this.dataset.itemCountOne || '1 item',
      this.dataset.itemCountOther || '2 items'
    );

    this.refs.productCount.textContent = countText;
    this.refs.mobileProductCount.textContent = countText;

    const seeResults = this.querySelector('[data-see-results]');

    if (seeResults instanceof HTMLElement) {
      seeResults.textContent = this.#formatCount(
        visibleProductCount,
        this.dataset.seeItemsOne || 'See 1 item',
        this.dataset.seeItemsOther || 'See 2 items'
      );
    }
  }

  #updateChrome() {
    const count = this.getters.getActiveFilterCount();
    const hasFilters = count > 0;

    this.refs.desktopClear.classList.toggle('facets__clear-all-link--active', hasFilters);

    const drawerClear = this.querySelector('[data-drawer-clear]');

    if (drawerClear instanceof HTMLElement) {
      drawerClear.classList.toggle('facets__clear-all--active', hasFilters);
    }

    this.#setCountBubble(this.refs.mobileCount, count);

    const drawerCount = this.querySelector('[data-drawer-count]');

    if (drawerCount instanceof HTMLElement) {
      this.#setCountBubble(drawerCount, count);
    }
  }

  /**
   * @param {HTMLElement} bubble
   * @param {number} count
   */
  #setCountBubble(bubble, count) {
    const text = bubble.querySelector('[data-count-text]');
    const label = this.#formatCount(
      count,
      this.dataset.filterCountOne || '1 filter applied',
      this.dataset.filterCountOther || '2 filters applied'
    );

    if (count > 0) {
      bubble.hidden = false;
      bubble.setAttribute('aria-label', label);

      if (text instanceof HTMLElement) {
        text.textContent = String(count);
      } else {
        bubble.textContent = String(count);
      }
    } else {
      bubble.hidden = true;
      bubble.removeAttribute('aria-label');

      if (text instanceof HTMLElement) {
        text.textContent = '';
      } else {
        bubble.textContent = '';
      }
    }
  }

  /**
   * @param {HTMLElement} card
   * @returns {ActiveFilters}
   */
  #readCardFilters(card) {
    try {
      return JSON.parse(card.dataset.filters || '{}');
    } catch {
      return emptyActiveFilters();
    }
  }

  /**
   * @param {number} count
   * @param {string} oneTemplate
   * @param {string} otherTemplate
   * @returns {string}
   */
  #formatCount(count, oneTemplate, otherTemplate) {
    const template = count === 1 ? oneTemplate : otherTemplate;

    return template.replace(/\d+/, String(count));
  }

  /**
   * @param {'caret' | 'checkmark'} name
   * @returns {Node}
   */
  #cloneTemplate(name) {
    const template = this.querySelector(`template[data-bb-${name}]`);

    if (template instanceof HTMLTemplateElement) {
      return template.content.cloneNode(true);
    }

    return document.createElement('span');
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #slug(value) {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }
}

if (!customElements.get('bundle-builder-filters-component')) {
  customElements.define('bundle-builder-filters-component', BundleBuilderFiltersComponent);
}
